/**
 * 图片打包下载进度弹窗组件
 * 采用异步队列方案:后端打包成ZIP,前端轮询进度并显示实时状态
 *
 * 业务流程:
 * 1. 用户点击下载按钮
 * 2. 后端创建打包任务,返回任务ID
 * 3. 前端轮询任务状态(每2秒)
 * 4. 显示打包进度(0-100%)
 * 5. 完成后显示ZIP下载链接
 */

import type { ReactElement } from 'react'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Package,
  FileArchive,
  Clock,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { createImagePackTask, pollImagePackTask } from '@/services/imagePackService'
import { IMAGE_PACK_REF_TYPE } from '@/types/imagePack'
import type { ImagePackRefType } from '@/types/imagePack'

/**
 * 打包任务状态枚举
 */
export const PACK_TASK_STATUS = {
  /** 排队中 */
  PENDING: 'pending',
  /** 处理中 */
  PROCESSING: 'processing',
  /** 已完成 */
  COMPLETED: 'completed',
  /** 失败 */
  FAILED: 'failed',
} as const

export type PackTaskStatus = (typeof PACK_TASK_STATUS)[keyof typeof PACK_TASK_STATUS]

/**
 * 打包任务信息
 */
export interface PackTaskInfo {
  /** 任务ID */
  id: string
  /** 任务状态 */
  status: PackTaskStatus
  /** 进度百分比 (0-100) */
  progress: number
  /** 已处理数量 */
  processed_count: number
  /** 总数量 */
  total_count: number
  /** 下载URL(仅completed状态) */
  zip_url?: string | null
  /** 文件大小(字节,仅completed状态) */
  zip_size?: number | null
  /** ZIP 文件名（不含扩展名） */
  zip_filename?: string
  /** 错误信息(仅failed状态) */
  error_message?: string | null
  /** 创建时间 */
  created_at: string
  /** 完成时间 */
  expired_at?: string | null
}

/**
 * 组件属性
 */
interface ImagePackDialogProps {
  /** 是否打开弹窗 */
  open: boolean
  /** 弹窗状态变化回调 */
  onOpenChange: (open: boolean) => void
  /** 关联ID */
  batchId: string
  /** 关联类型，默认 batch_task */
  refType?: ImagePackRefType
  /** 任务备注(用于显示) */
  remark?: string
  /** 预期图片数量 */
  totalCount: number
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 B'
  }
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i] ?? 'B'}`
}

/**
 * 状态图标组件
 */
interface StatusIconProps {
  status: PackTaskStatus
}

function StatusIcon({ status }: StatusIconProps): ReactElement {
  switch (status) {
    case PACK_TASK_STATUS.PENDING:
      return <Clock className="h-12 w-12 text-amber-500" />
    case PACK_TASK_STATUS.PROCESSING:
      return <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
    case PACK_TASK_STATUS.COMPLETED:
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <CheckCircle2 className="h-12 w-12 text-emerald-500" />
        </motion.div>
      )
    case PACK_TASK_STATUS.FAILED:
      return <XCircle className="h-12 w-12 text-red-500" />
    default:
      return <Package className="h-12 w-12 text-muted-foreground" />
  }
}

/**
 * 状态文案配置
 */
function getStatusText(status: PackTaskStatus): {
  title: string
  description: string
} {
  switch (status) {
    case PACK_TASK_STATUS.PENDING:
      return {
        title: '排队中',
        description: '您的打包任务已进入队列,请稍候...',
      }
    case PACK_TASK_STATUS.PROCESSING:
      return {
        title: '正在打包',
        description: '正在将图片打包成ZIP文件，可关闭弹窗后在下载中心查看进度',
      }
    case PACK_TASK_STATUS.COMPLETED:
      return {
        title: '打包完成',
        description: '图片已打包完成,点击下方按钮下载',
      }
    case PACK_TASK_STATUS.FAILED:
      return {
        title: '打包失败',
        description: '打包过程中出现错误,请重试',
      }
    default:
      return {
        title: '准备中',
        description: '正在初始化打包任务...',
      }
  }
}

/**
 * 图片打包下载进度弹窗组件
 * @param props 组件属性
 * @returns 弹窗 ReactElement
 */
export function ImagePackDialog({
  open,
  onOpenChange,
  batchId,
  refType = IMAGE_PACK_REF_TYPE.BATCH_TASK,
  remark,
  totalCount,
}: ImagePackDialogProps): ReactElement {
  // 打包任务信息
  const [taskInfo, setTaskInfo] = useState<PackTaskInfo | null>(null)
  // 是否正在初始化(创建任务)
  const [isInitializing, setIsInitializing] = useState(false)
  // 错误信息
  const [error, setError] = useState<string | null>(null)

  // 取消轮询函数引用
  const cancelPollingRef = useRef<(() => void) | null>(null)
  // 是否已初始化标志
  const hasInitializedRef = useRef(false)

  /**
   * 初始化打包任务
   */
  const initializePackTask = useCallback(async (): Promise<void> => {
    if (isInitializing || hasInitializedRef.current) {
      return
    }

    hasInitializedRef.current = true
    setIsInitializing(true)
    setError(null)

    try {
      // 创建打包任务
      const result = await createImagePackTask({
        ref_type: refType,
        ref_id: batchId,
      })

      // 设置初始任务信息
      setTaskInfo({
        id: result.id,
        status: result.status as PackTaskStatus,
        progress: result.progress,
        processed_count: result.processed_count,
        total_count: result.total_count,
        ...(result.zip_filename !== undefined ? { zip_filename: result.zip_filename } : {}),
        created_at: result.created_at,
      })

      // 开始轮询
      const cancel = pollImagePackTask(
        result.id,
        (task): void => {
          setTaskInfo({
            id: task.id,
            status: task.status as PackTaskStatus,
            progress: task.progress,
            processed_count: task.processed_count,
            total_count: task.total_count,
            zip_url: task.zip_url,
            zip_size: task.zip_size,
            ...(task.zip_filename !== undefined ? { zip_filename: task.zip_filename } : {}),
            error_message: task.error_message,
            created_at: task.created_at,
            expired_at: task.expired_at,
          })
        }
      )
      cancelPollingRef.current = cancel
    } catch (err) {
      const message = err instanceof Error ? err.message : '创建打包任务失败'
      setError(message)
    } finally {
      setIsInitializing(false)
    }
  }, [isInitializing, batchId, refType])

  /**
   * 弹窗打开时初始化任务
   */
  useEffect(() => {
    if (open && !hasInitializedRef.current) {
      void initializePackTask()
    }
  }, [open, initializePackTask])

  /**
   * 弹窗关闭时清理
   */
  useEffect(() => {
    if (!open) {
      if (cancelPollingRef.current !== null) {
        cancelPollingRef.current()
        cancelPollingRef.current = null
      }
      setTaskInfo(null)
      setError(null)
      setIsInitializing(false)
      hasInitializedRef.current = false
    }
  }, [open])

  /**
   * 组件卸载时清理
   */
  useEffect(() => {
    return (): void => {
      if (cancelPollingRef.current !== null) {
        cancelPollingRef.current()
      }
    }
  }, [])

  /**
   * 处理下载按钮点击
   */
  const handleDownload = useCallback((): void => {
    if (taskInfo?.zip_url !== undefined && taskInfo.zip_url !== null) {
      // 使用隐藏的a标签触发下载
      const link = document.createElement('a')
      link.href = taskInfo.zip_url
      const preferredName =
        taskInfo.zip_filename !== undefined && taskInfo.zip_filename !== ''
          ? taskInfo.zip_filename
          : remark !== undefined && remark !== ''
            ? remark
            : 'images'
      link.download = `${preferredName}.zip`
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [taskInfo, remark])

  /**
   * 处理重试
   */
  const handleRetry = useCallback((): void => {
    hasInitializedRef.current = false
    void initializePackTask()
  }, [initializePackTask])

  /**
   * 处理关闭
   */
  const handleClose = useCallback((): void => {
    onOpenChange(false)
  }, [onOpenChange])

  // 状态文案
  const statusText = taskInfo !== null ? getStatusText(taskInfo.status) : { title: '准备中', description: '' }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            打包下载
          </DialogTitle>
          <DialogDescription>
            {remark !== undefined && remark !== '' ? remark : `共 ${String(totalCount)} 张图片`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-6">
          {/* 错误状态 */}
          {error !== null && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </motion.div>
          )}

          {/* 初始化状态 */}
          {isInitializing && taskInfo === null && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">正在创建打包任务...</p>
            </div>
          )}

          {/* 任务进行中 */}
          {taskInfo !== null && error === null && (
            <AnimatePresence mode="wait">
              <motion.div
                key={taskInfo.status}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-6"
              >
                {/* 状态图标和文案 */}
                <div className="flex flex-col items-center gap-3 text-center">
                  <StatusIcon status={taskInfo.status} />
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold">{statusText.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {statusText.description}
                    </p>
                  </div>
                </div>

                {/* 进度条 */}
                {(taskInfo.status === PACK_TASK_STATUS.PENDING ||
                  taskInfo.status === PACK_TASK_STATUS.PROCESSING) && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        已处理: {taskInfo.processed_count}/{taskInfo.total_count}
                      </span>
                      <span className="font-semibold text-primary">
                        {taskInfo.progress}%
                      </span>
                    </div>
                    <Progress
                      value={taskInfo.progress}
                      className="h-3"
                    />
                  </div>
                )}

                {/* 完成状态 - 文件信息 */}
                {taskInfo.status === PACK_TASK_STATUS.COMPLETED && taskInfo.zip_size !== undefined && taskInfo.zip_size !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-center gap-3 rounded-lg border bg-muted/50 p-4"
                  >
                    <FileArchive className="h-8 w-8 text-emerald-500" />
                    <div className="text-sm">
                      <p className="font-medium">ZIP文件已准备就绪</p>
                      <p className="text-muted-foreground">
                        文件大小: {formatFileSize(taskInfo.zip_size)}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* 失败状态 - 错误信息 */}
                {taskInfo.status === PACK_TASK_STATUS.FAILED && taskInfo.error_message !== undefined && taskInfo.error_message !== null && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{taskInfo.error_message}</AlertDescription>
                  </Alert>
                )}

                {/* 温馨提示 */}
                {(taskInfo.status === PACK_TASK_STATUS.PENDING ||
                  taskInfo.status === PACK_TASK_STATUS.PROCESSING) && (
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        打包需要一些时间,您可以最小化此窗口继续浏览,任务将在后台继续进行。
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        <DialogFooter>
          {/* 重试按钮(仅失败或错误时显示) */}
          {(taskInfo?.status === PACK_TASK_STATUS.FAILED || error !== null) && (
            <Button variant="outline" onClick={handleRetry}>
              重试
            </Button>
          )}

          <Button
            asChild
            variant="outline"
            onClick={(): void => {
              onOpenChange(false)
            }}
          >
            <Link to={`/records/downloads?ref_type=${refType}&ref_id=${batchId}`}>查看下载中心</Link>
          </Button>

          {/* 下载按钮(仅完成时显示) */}
          {taskInfo?.status === PACK_TASK_STATUS.COMPLETED && (
            <Button onClick={handleDownload} className="gap-2">
              <Download className="h-4 w-4" />
              下载ZIP文件
            </Button>
          )}

          {/* 关闭按钮 */}
          <Button variant="secondary" onClick={handleClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
